import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import { NotificationProvider } from '../contexts/NotificationContext';
import { GridProvider } from '../contexts/GridContext';
import { isTestDomain, isProductionDomain, isAdminDomain } from '../utils/domain';
import { API_ENDPOINTS } from '../config/api';
import ConstructionGate from '../components/ConstructionGate';
import Home from '../pages/Home';
import Listing from '../pages/Listing';
import Profile from '../pages/Profile';
import EditProfile from '../pages/EditProfile/EditProfile';
import Messages from '../pages/Messages';
import Create from '../pages/Create';
import Edit from '../pages/Edit';
import SignUp from '../pages/SignUp';
import ForgotPassword from '../pages/ForgotPassword/ForgotPassword';
import ResetPassword from '../pages/ResetPassword/ResetPassword';
import Terms from '../pages/Terms';
import Privacy from '../pages/Privacy';
import ClaimListing from '../pages/ClaimListing';
import './App.css';

// Lazy load Admin component - only load when needed (not in production bundle unless accessed)
const Admin = React.lazy(() => import('../pages/Admin'));

const AdminSuspenseFallback: React.FC = () => {
    return (
        <div className="admin-fallback-container">
            <div className="admin-fallback-wrapper">
                <div className="admin-fallback-relative">
                    {/* 3x3 Dots Grid */}
                    <div className="admin-fallback-dots-grid">
                        {Array.from({ length: 9 }).map((_, i) => (
                            <div key={i} className="admin-fallback-dot-wrapper">
                                <div className="admin-fallback-dot" />
                            </div>
                        ))}
                    </div>
                    {/* Left and Right Button Placeholders */}
                    <div className="admin-fallback-button admin-fallback-button-left" />
                    <div className="admin-fallback-button admin-fallback-button-right" />
                    {/* Centered Dot Placeholder */}
                    <div className="admin-fallback-status-dot-placeholder" />
                </div>
            </div>
        </div>
    );
};


function MsalRedirectHandler() {
    const { instance, inProgress, accounts } = useMsal();
    const { loginWithMicrosoft, showGoogleSetupModal, isAuthenticated } = useAuth();
    const [hasAttemptedLogin, setHasAttemptedLogin] = React.useState(false);

    useEffect(() => {
        // If MSAL has finished its work, found an account, but our backend doesn't know the user yet
        if (inProgress === 'none' && accounts.length > 0 && !isAuthenticated && !hasAttemptedLogin) {
            setHasAttemptedLogin(true); // Prevent infinite loops if backend login fails

            instance.acquireTokenSilent({
                scopes: ['openid', 'profile', 'email'],
                account: accounts[0]
            }).then(response => {
                console.log("Acquired MSAL token, sending to backend...");
                loginWithMicrosoft(response.idToken).then(result => {
                    if (result?.requiresSetup) {
                        showGoogleSetupModal(result.setupData);
                    }
                }).catch(err => {
                    console.error("Microsoft backend login failed:", err);
                });
            }).catch(err => {
                console.error("Failed to acquire token silently:", err);
            });
        }
    }, [inProgress, accounts, instance, isAuthenticated, hasAttemptedLogin, loginWithMicrosoft, showGoogleSetupModal]);

    return null;
}


function AppContent() {
    const isAdmin = isAdminDomain();
    const isTest = isTestDomain();
    const isProduction = isProductionDomain();
    const location = useLocation();
    const isAdminRoute = location.pathname === '/admin';
    // Only allow /admin route in development (localhost) or on usubx.com domain
    // NEVER allow it on production domains
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const canAccessAdminRoute = isAdminRoute && (isAdmin || isLocalhost) && !isProduction;

    // Disable service worker so the site always pulls fresh assets.
    useEffect(() => {
        if ('serviceWorker' in navigator) {
            // Unregister all service workers
            navigator.serviceWorker.getRegistrations().then((registrations) => {
                registrations.forEach((registration) => {
                    console.log('Unregistering service worker');
                    registration.unregister();
                });
            });

            // Clear all caches to prevent blank page issues
            if ('caches' in window) {
                caches.keys().then((cacheNames) => {
                    cacheNames.forEach((cacheName) => {
                        console.log('Deleting cache:', cacheName);
                        caches.delete(cacheName);
                    });
                });
            }
        }
    }, []);

    // Track page visits (only on non-admin pages)
    useEffect(() => {
        if (!isAdmin && !isAdminRoute) {
            // Track visit asynchronously without blocking
            fetch(`${API_ENDPOINTS.ADMIN.STATS.replace('/stats', '/track-visit')}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).catch(() => {
                // Silently fail if tracking doesn't work
            });
        }
    }, [location.pathname, isAdmin, isAdminRoute]);

    // SECURITY: Block admin access on production domains
    useEffect(() => {
        if (isAdminRoute && isProduction && !isAdmin) {
            // If on production domain and trying to access /admin, immediately redirect to home
            // This prevents any admin code from loading
            window.location.replace('/');
        }
    }, [isAdminRoute, isProduction, isAdmin]);

    // SECURITY: Additional check - if somehow admin route is accessible on production, block it
    if (isProduction && !isAdmin && isAdminRoute) {
        // Return early - don't render anything, redirect will happen
        return null;
    }

    return (
        <div className={(isAdmin || (isAdminRoute && canAccessAdminRoute)) ? "App App-admin-isolated" : "App"}>
            <Routes>
                {/* Admin domain routes - usubx.com */}
                {isAdmin && (
                    <Route
                        path="/"
                        element={
                            <Suspense fallback={<AdminSuspenseFallback />}>
                                <Admin />
                            </Suspense>
                        }
                    />
                )}

                {/* Admin panel route - ONLY accessible at /admin for local development or usubx.com */}
                {/* NEVER accessible on production domains - redirects to home if accessed */}
                {canAccessAdminRoute && (
                    <Route
                        path="/admin"
                        element={
                            <Suspense fallback={<AdminSuspenseFallback />}>
                                <Admin />
                            </Suspense>
                        }
                    />
                )}

                {/* Catch-all for /admin on production - redirect to home */}
                {isProduction && !isAdmin && <Route path="/admin" element={<Home />} />}

                {/* Production and test domain routes */}
                {!isAdmin && <Route path="/" element={<Home />} />}
                {!isAdmin && <Route path="/listing/:id" element={<Listing />} />}
                {!isAdmin && <Route path="/profile" element={<Profile />} />}
                {!isAdmin && <Route path="/profile/edit" element={<EditProfile />} />}
                {!isAdmin && <Route path="/messages" element={<Messages />} />}
                {!isAdmin && <Route path="/create" element={<Create />} />}
                {!isAdmin && <Route path="/listing/:id/edit" element={<Edit />} />}
                {!isAdmin && <Route path="/edit/:id" element={<Edit />} />}
                {!isAdmin && <Route path="/signup" element={<SignUp />} />}
                {!isAdmin && <Route path="/forgot" element={<ForgotPassword />} />}
                {!isAdmin && <Route path="/reset-password" element={<ResetPassword />} />}
                {!isAdmin && <Route path="/terms" element={<Terms />} />}
                {!isAdmin && <Route path="/privacy" element={<Privacy />} />}
                {!isAdmin && <Route path="/college/:slug" element={<Home />} />}
                {isProductionDomain() && <Route path="/claim/:id" element={<ClaimListing />} />}
            </Routes>
        </div>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <NotificationProvider>
                    <GridProvider>
                        <Router>
                            <ConstructionGate>
                                <MsalRedirectHandler />
                                <AppContent />
                            </ConstructionGate>
                        </Router>
                    </GridProvider>
                </NotificationProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
